/**
 * Marketing-surface metadata for shipped templates.
 *
 * Source of truth for the templates section on the landing AND each
 * template's detail page. Keep this in sync with the actual configs in
 * `app/data/*.config.json` (slug, file types, etc.). Marketing copy
 * is intentionally separate from config copy — the configs are
 * functional, this is positioning.
 */

export type TemplateStatus = 'Available' | 'Preview' | 'Coming soon'

/** A section in a public legal/policy page (e.g. data-retention policy). */
export interface LegalPageSection {
  id: string
  title: string
  /** Markdown-ish: blank-line-separated paragraphs, `- ` bullets, `**bold**`,
   *  and inline HTML are all the renderer in legal.$page.tsx understands. */
  body: string
}

/** A self-contained legal/policy page attached to a template. */
export interface LegalPage {
  /** URL slug under /legal/<slug>. Unique across all templates. */
  slug: string
  title: string
  /** Sub-headline shown under the title. */
  description?: string
  /** Bump when commitments change; rendered + cited by licensors / auditors. */
  version: string
  sections: LegalPageSection[]
}

export interface MarketingTemplate {
  slug: string // URL slug under /templates/<slug>
  configId: string // matches app/data/<configId>.config.json
  eyebrow: string // e.g. "Business"
  title: string // e.g. "Real Estate"
  tagline: string // one-line positioning
  status: TemplateStatus
  /** Personal-data templates you'd want to run privately — surfaces a
   *  "Local-first" badge. Pairs with the home "Your data, your terms" story. */
  localMode?: boolean
  body: string // card body on the home page
  who: string // who it's for, used on the detail page
  tracks: string[] // bullet list of what gets tracked
  example: {
    user: string
    result: string
    actions?: string[]
  }
  /** Detail-page screenshots. Drop PNGs at the referenced paths. */
  screenshots: {
    src?: string // optional; falls back to placeholder
    alt: string
    caption?: string
    aspect?: string
  }[]
  /** Public legal pages owned by this template (e.g. an MLS data retention
   *  policy for real-estate). Rendered by the generic /legal/<slug> route. */
  legalPages?: LegalPage[]
}

export const templates: MarketingTemplate[] = [
  {
    slug: 'real-estate',
    configId: 'business.residential-realestate',
    eyebrow: 'Business',
    title: 'Real Estate',
    tagline: 'Every offer, every client, every closing — kept.',
    status: 'Available',
    body: 'Buys, sells, listings, clients, commissions, escrow timelines. Every offer round becomes history — not just today’s number.',
    who: 'Agents and small teams running residential transactions. Useful if you have ever lost track of which buyer is in which stage, or which counter was the one before the one that closed.',
    tracks: [
      'Buy / sell transactions with full status pipeline',
      'Client and lead files linked across deals',
      'Listings synced from MLS (where supported)',
      'Commissions, fees, and net-sheet math kept live',
      'Escrow milestone dates and inspection windows',
    ],
    example: {
      user: 'Met the Garcias today, pre-approved $600k.',
      result: 'Updated the Garcia buy file.',
      actions: [
        'Added a note with today’s date',
        'Set pre-approval amount: $600,000',
        'Status moved to Active',
      ],
    },
    screenshots: [
      {
        alt: 'Pipeline dashboard — active buy and sell transactions by stage',
        caption: 'Pipeline view across active transactions.',
        aspect: '16/9',
      },
      {
        alt: 'Buy transaction detail — offer history with every value',
        caption: 'Offer history: every counter, kept.',
        aspect: '4/3',
      },
      {
        alt: 'Client file with linked transactions and contact timeline',
        caption: 'A client, across years of work.',
        aspect: '4/3',
      },
    ],
    legalPages: [
      {
        slug: 'mls-data-retention',
        title: 'MLS Data Retention Policy',
        description:
          'How Quiver Labs handles MLS Content for workspaces licensed against an MLS data feed.',
        version: '2026-05-29',
        sections: [
          {
            id: 'scope',
            title: 'Scope',
            body: `This policy describes how Quiver Labs handles "MLS Content" - listing data, photos (URLs and any cached thumbnails), remarks, field values, and any other MLS-supplied information - for workspaces operated under a data license with a Multiple Listing Service (e.g. MLSListings, Inc. for the Bay Area).

It applies only to workspaces configured for licensed real-estate use under a verified Broker license; it does not apply to other Quiver workspaces, which never receive MLS Content.`,
          },
          {
            id: 'storage-minimum',
            title: 'Storage minimum (thin file, ephemeral re-fetch)',
            body: `Quiver stores the minimum MLS Content needed for cross-file aggregation, search, and a file's identity. The rest is re-fetched live from the MLS feed when a user opens a detail view.

What we store on a saved listing or comparable:
- Source identifier (which MLS) and external record id
- Address, beds, baths, sqft, property type
- Sold price + sold date (for comparables)
- Listing brokerage and source-provided disclaimer (for attribution)
- A cached cover-photo thumbnail (re-fetchable; never the full gallery as bytes)

What we re-fetch live each time it's viewed:
- Full photo gallery
- Public remarks / descriptions
- School data
- Current status, price, days on market`,
          },
          {
            id: 'cache',
            title: 'Live-search cache (ephemeral)',
            body: `Results returned by a live MLS search are held in an in-memory, per-workspace cache for **no more than 10 seconds** to serve a single user interaction. Search results are **never written to disk** as files unless an authorized agent explicitly saves an individual listing (e.g. as a CMA comparable or a buyer's tour).`,
          },
          {
            id: 'materialized',
            title: 'Materialized listings',
            body: `When an authorized agent saves a listing into a workspace (as a comp, a tour, or a property of interest), Quiver creates a file inside that workspace carrying the minimum fields above plus the source attribution. These files are retained for **the life of the workspace** and can be deleted at any time by their owner or by a workspace admin from the file menu.

A Subscriber may request deletion at any time, including of a single materialized listing, all materialized listings in a workspace, or all materialized listings across every workspace under the brokerage's license. **Quiver will purge the requested files within 30 days of the request** and send the requester a deletion confirmation.`,
          },
          {
            id: 'subscriber-revocation',
            title: 'Subscriber revocation',
            body: `When a Subscriber's access to the underlying MLS is revoked, or the brokerage's data licensing agreement is terminated, expired, or marked revoked in the Quiver workspace's license record, the affected workspaces are flagged immediately, and **all materialized MLS Content is purged within 14 days**. A deletion confirmation is available to the MLS on request.

The platform-admin "Purge licensed data" action surfaces in the workspace's License settings the moment the license becomes inactive, so the purge can be executed on-demand and does not depend on the 14-day window.`,
          },
          {
            id: 'ai',
            title: 'AI usage of MLS Content',
            body: `Quiver uses third-party large language models (Anthropic's Claude family, via the Anthropic API in inference-only configuration) for narrow in-workspace agent features: summarizing user-attached documents, extracting structured fields from natural-language input, and answering scoped questions about a user's own workspace.

- MLS Content may appear in an inference prompt when needed to satisfy a user's specific request; it is never used to train, fine-tune, or update any model.
- Anthropic's Commercial Terms, **Section B (Customer Content)**, state that *"Anthropic may not train models on Customer Content from Services."* Section E.1 (Confidential Information) further classifies Customer Content as Customer's Confidential Information. We have additionally requested a Zero Data Retention agreement for the Quiver Labs organization, which once active eliminates at-rest retention by the model provider beyond what's needed for abuse screening.
- We do not produce or store embeddings or vector representations of MLS Content.
- AI request/response logs in Quiver's operational telemetry are retained for **no more than 30 days** and are scrubbed of MLS Content before any retention beyond that window.`,
          },
          {
            id: 'attribution',
            title: 'Attribution',
            body: `Every listing displayed in a Quiver workspace, whether in ephemeral search results, the saved-listing detail view, or a comparison view, carries the listing brokerage and source-provided disclaimer, in accordance with the MLS's Display Guidelines.`,
          },
          {
            id: 'no-redistribution',
            title: 'No redistribution',
            body: `MLS Content stays inside the controlled environment of the workspace it belongs to. Quiver does not publish, syndicate, or otherwise expose MLS Content to any third party, search engine, or non-Subscriber. There is no public-facing portal: every workspace user is authenticated, and clients access only the workspace their agent has explicitly invited them to.`,
          },
          {
            id: 'no-external-storage',
            title: 'No external third-party storage',
            body: `MLS Content is stored only in Quiver's own per-workspace database, behind access controls. We do not use any external third-party vector database, RAG store, embeddings provider, analytics platform, or logging service that would receive MLS Content or its derivatives.`,
          },
          {
            id: 'requests',
            title: 'Requests & contact',
            body: `MLSes, brokerages, and Subscribers may request a deletion confirmation, an audit of our handling of their content, or any other clarification by contacting Quiver Labs at <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="mailto:hello@quiveros.ai">hello@quiveros.ai</a>. We respond within 5 business days.`,
          },
        ],
      },
    ],
  },
  {
    slug: 'finance',
    configId: 'personal.finance',
    eyebrow: 'Personal',
    title: 'Personal Finance',
    tagline:
      'Net worth you can rewind — every balance it ever passed through.',
    status: 'Coming soon',
    localMode: true,
    body: 'Accounts, transactions, budgets, assets, debts. Net worth isn’t just today’s number — it’s reconstructed month by month from balance history. Cloud or local: the money data too personal for someone else’s cloud stays on your machine.',
    who: 'Anyone who wants their whole financial picture in one place without handing it to a budgeting startup. Especially good if you want the data local and the math live — and if you’ve ever wished you could see what your net worth actually was last March, not just guess.',
    tracks: [
      'Accounts and transactions synced via Plaid (sandbox today)',
      'Net worth as an as-of-date snapshot, rebuilt month-by-month from balance history',
      'Monthly and quarterly budgets with a live limit-minus-spent receipt',
      'Spend by category and cash flow (income vs. spend) over any period',
      'Assets and liabilities — house, car, mortgage, loans — folded into net worth',
      'Cloud or local: financial data computed on your own instance',
    ],
    example: {
      user: 'Categorize my Whole Foods charge as groceries and show me this month’s spend.',
      result: 'Updated the transaction and read your Food & Drink total.',
      actions: [
        'Category set to Food & Drink',
        'Spend by category for this month, broken out',
        'Net worth over time, reconstructed from balance history',
      ],
    },
    screenshots: [
      {
        alt: 'Money dashboard — net worth, spend and income this period, cash flow',
        caption: 'Your money at a glance — net worth, cash flow, spend by category.',
        aspect: '16/9',
      },
      {
        alt: 'Net worth over time line chart rebuilt month-by-month from balance history',
        caption: 'Net worth over time, reconstructed from balance history.',
        aspect: '4/3',
      },
      {
        alt: 'Budget file with the Budget vs. actual receipt — limit minus spent, live',
        caption: 'Budget vs. actual: limit minus spent, computed live so it can’t drift.',
        aspect: '4/3',
      },
    ],
    legalPages: [
      {
        slug: 'financial-data',
        title: 'Financial Account Data Policy',
        description:
          'How Quiver Labs handles financial account data accessed through Plaid for workspaces that connect financial accounts.',
        version: '2026-05-31',
        sections: [
          {
            id: 'scope',
            title: 'Scope',
            body: `This policy describes how Quiver Labs handles financial account data for workspaces that connect financial accounts (for example, the personal finance template). It applies only to those workspaces; other Quiver workspaces never receive financial account data. It supplements our general <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="/legal/privacy">Privacy Policy</a>.`,
          },
          {
            id: 'plaid',
            title: 'How we connect your accounts (Plaid)',
            body: `To let you connect your accounts, we use <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="https://plaid.com">Plaid Inc.</a> ("Plaid"). When you link an account, you enter your bank credentials directly into Plaid's secure flow — **Quiver never sees, receives, or stores your online banking username or password.**

Plaid's handling of the data it collects is governed by Plaid's own End User Privacy Policy, available at <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="https://plaid.com/legal/#end-user-privacy-policy">plaid.com/legal</a>. By connecting an account, you also agree to Plaid's policy.`,
          },
          {
            id: 'what-we-receive',
            title: 'What we receive and store',
            body: `Plaid returns to us only the data needed to provide the feature:
- Account details — name, type, masked account number, and institution
- Balances
- Transaction history

This data is stored in your own per-workspace database behind access controls. We do not store your banking credentials, and we do not create embeddings or vector representations of your financial data in any third-party store.`,
          },
          {
            id: 'how-we-use',
            title: 'How we use it',
            body: `We use your financial data **solely to power your own dashboards** for you as the account owner — aggregating balances into a net-worth view, categorizing transactions, tracking spending against budgets, and reconstructing your financial history over time.

**We do not sell, rent, or share your financial data**, and we never use it for advertising or to build profiles for sale. It is used only to deliver the features you asked for.`,
          },
          {
            id: 'your-control',
            title: 'Your control & deletion',
            body: `You are always in control of your connected accounts and the data they bring in:
- Disconnect any linked account at any time from within Quiver to stop new data from being pulled.
- Delete individual transactions, files, or your entire workspace to remove the underlying data.
- Manage or revoke Plaid's connections directly through the <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="https://my.plaid.com">Plaid Portal</a>.
- Request full deletion by contacting <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="mailto:hello@quiveros.ai">hello@quiveros.ai</a>; we purge the requested data within 30 days and confirm when done.`,
          },
        ],
      },
    ],
  },
  {
    slug: 'product-development',
    configId: 'business.product-development',
    eyebrow: 'Teams',
    title: 'Product Development',
    tagline: 'Bug tracker, but the bugs explain themselves.',
    status: 'Available',
    body: 'Issues, severity, area, assignees, links. We use it to track Quiver’s own bugs — proof the platform is general, not a real-estate tool with aspirations.',
    who: 'Small product teams who want issue tracking without the heavyweight tool. Especially good for teams that want AI to triage, summarize, and route work — not just store it.',
    tracks: [
      'Issues with severity, area, and kind',
      'Status workflow: reported → triaged → in progress → review → done',
      'Assignees as linked people',
      'Duplicate-of, blocked-by, relates-to between issues',
      'Open-by-severity and weekly intake metrics',
    ],
    example: {
      user: 'Found a bug — login form crashes on Safari when 1Password autofills.',
      result: 'Created issue #142.',
      actions: [
        'Severity: high · Area: ui · Kind: bug',
        'Status: reported (awaiting triage)',
        'Browser and OS captured in system_info',
      ],
    },
    screenshots: [
      {
        src: '/marketing/pd-overview.png',
        alt: 'Product Development overview — open issues, weekly intake, intake vs resolved, and breakdowns by severity, area, and assignee',
        caption: 'The whole pipeline at a glance.',
        aspect: '16/9',
      },
      {
        src: '/marketing/pd-board.png',
        alt: 'Issue board grouped by status — reported, triaged, in progress, in review',
        caption: 'Issues by status, kanban-style.',
        aspect: '4/3',
      },
      {
        src: '/marketing/pd-issue-detail.png',
        alt: 'Issue detail — severity, area, assignee, and the AI-extracted summary',
        caption: 'Every issue, structured.',
        aspect: '4/3',
      },
    ],
  },
  {
    slug: 'health-fitness',
    configId: 'personal.health-and-fitness',
    eyebrow: 'Personal',
    title: 'Health & Fitness',
    tagline: 'Tell it what you lifted. It keeps every rep, every PR, every lab.',
    status: 'Preview',
    localMode: true,
    body: 'A real training log with teeth: a reusable exercise library, workout sessions that move through planned → warm-up → working → cool-down → complete, and per-set entries (weight, reps, volume, tempo, to-failure, supersets). Injuries route the coach around hurt movements; body metrics and lab panels trend for the doctor. Same file, three lenses — athlete, coach, doctor — and it can stay on your own machine.',
    who: "Anyone who trains seriously and is tired of three apps that forget — plus the coach and doctor they want reading the same data through their own lens. Local-first friendly when your labs and bloodwork are too personal for someone else's cloud.",
    tracks: [
      'Reusable exercise library — muscle group, equipment, movement pattern, rep ranges',
      'Workout sessions through a live phase flow: planned → warm-up → working → cool-down → complete',
      'Per-set entries: working weight, reps, sets, volume (weight × reps × sets), tempo, to-failure, supersets, intensity techniques',
      'Per-movement PRs, the Big 3, and the 1k Club (squat + bench + deadlift)',
      'Injuries with their own flow — flared → rehabbing → managed → recovered — and the movements to avoid',
      'Body metrics (weight, body fat, resting HR, blood pressure, VO2 max) and lab panels (LDL/HDL, A1c, vitamin D, testosterone, TSH) trended over time, per family member',
    ],
    example: {
      user: 'Bench 3x8 at 185 today, last set to failure.',
      result: "Logged it on today's workout.",
      actions: [
        'Exercise entry: bench 185 lb × 8 × 3, to failure — volume 4,440 lb',
        'Hit the top of your rep range — add weight next bench session',
        'New best counts toward your Bench PR and the 1k Club',
      ],
    },
    screenshots: [
      {
        src: '/marketing/hf-overview.png',
        alt: 'Wellness dashboard — total workouts, volume lifted, active injuries, the 1k Club progress bar and Big 3',
        caption: 'Total workouts, volume, the 1k Club, PRs by movement.',
        aspect: '16/9',
      },
      {
        src: '/marketing/hf-labs.png',
        alt: 'Lab panels — total cholesterol, fasting glucose, and HbA1c trending over time',
        caption: 'Bloodwork, charted over time.',
        aspect: '4/3',
      },
      {
        src: '/marketing/hf-coach-lens.png',
        alt: 'The same dashboard seen through a coach’s lens — training-focused metrics',
        caption: 'Same file. Different lens for your coach.',
        aspect: '4/3',
      },
    ],
  },
  {
    slug: 'film-journal',
    configId: 'personal.film-journal',
    eyebrow: 'Personal',
    title: 'Movie Database',
    tagline: 'Your own movie database — browse, rate, and keep everything you loved.',
    status: 'Preview',
    localMode: true,
    body: 'Search a real movie database (TMDB), browse by genre, and add a film with one tap — poster, backdrop, overview and score come along for the ride. Rate it right from the results, log where you saw it, and keep a one-line takeaway. Built entirely on Quiver’s generic listings primitive — the same machinery that renders MLS homes renders movies.',
    who: 'Anyone who keeps a watchlist in their head and forgets the good ones. Especially good if you want a real, media-rich catalog — posters and all — without hand-typing every title.',
    tracks: [
      'A searchable, browsable movie catalog (powered by TMDB) — posters, backdrops, overviews',
      'Add a film from search or genre-browse straight into your journal',
      'Status flow: want to watch → watching → watched → abandoned',
      'Your own 1–10 rating alongside the TMDB community score',
      'Where you saw it (theater / streaming / physical), rewatch flag, one-line takeaway',
      'Films watched, average rating, and minutes watched — kept live',
    ],
    example: {
      user: 'Add Blade Runner 2049 to my watchlist.',
      result: 'Found it on TMDB and added it to your journal.',
      actions: [
        'Created a film file with poster, overview, year and TMDB score',
        'Status set to Want to Watch',
        'Ready for your rating once you’ve seen it',
      ],
    },
    screenshots: [
      {
        alt: 'Discover grid — movie posters in a browsable grid with TMDB scores',
        caption: 'Browse and search a real movie catalog — posters and all.',
        aspect: '16/9',
      },
      {
        alt: 'Film detail drawer — backdrop, overview, score, and an Add to journal action',
        caption: 'Tap a film: backdrop, overview, score — then add it.',
        aspect: '4/3',
      },
      {
        alt: 'Watchlist and Seen views with your own ratings next to the community score',
        caption: 'Your rating, kept next to the crowd’s.',
        aspect: '4/3',
      },
    ],
    legalPages: [
      {
        slug: 'movie-data',
        title: 'Movie Data & Attribution',
        description:
          'How the Movie Database template uses The Movie Database (TMDB) API.',
        version: '2026-06-01',
        sections: [
          {
            id: 'attribution',
            title: 'TMDB attribution',
            body: `The Movie Database template uses the TMDB API to search and display movie information (titles, posters, backdrops, overviews, and community scores). **This product uses the TMDB API but is not endorsed or certified by TMDB.** All movie metadata and imagery are © their respective owners and are served from TMDB.`,
          },
          {
            id: 'your-key',
            title: 'Your own API token',
            body: `Movie data loads only after you add your own TMDB API Read Access Token in the workspace's secret settings. The token is stored encrypted on your own instance and is sent only to TMDB to authorize your searches. Without a token, the Discover and Search views simply show nothing.`,
          },
          {
            id: 'non-commercial',
            title: 'Personal, non-commercial use',
            body: `This template is intended for personal, non-commercial use under TMDB's terms. It is display-only and does not use TMDB data to train or fine-tune any model. Commercial use of the TMDB API requires a separate commercial agreement with TMDB.`,
          },
        ],
      },
    ],
  },
  {
    slug: 'drop-watch',
    configId: 'personal.watches',
    eyebrow: 'Personal',
    title: 'Drop Watch',
    tagline: 'Know the minute tickets drop — before they sell out.',
    status: 'Available',
    localMode: true,
    body: 'Race entries, event tickets, campsite releases — the ones that sell out in an hour, with no warning. Point a watch at the event page, say what you’re after ("women’s doubles"), and Quiver checks every few minutes. The moment your category goes on sale your phone buzzes with the ticket, the price, and the link — you book it by hand in thirty seconds. No bots, no auto-checkout: just never missing the window again.',
    who: 'Anyone chasing high-demand drops: HYROX and marathon entries, concert onsales, campsite and permit releases.',
    tracks: [
      'A watch per thing you’re chasing — event page, category to match, checkout link',
      'Availability checked every few minutes while the watch is open',
      'A push notification the instant your category goes on sale, with name and price',
      'Watching → available → booked, so you can see what you caught and what you missed',
    ],
    example: {
      user: 'My wife and her friend want to do HYROX Toronto women’s doubles — tickets aren’t out yet.',
      result: 'Watching HYROX Toronto for "DOUBLES WOMEN" — you’ll get a push the minute registration opens.',
      actions: [
        'Found the event’s ticket page and set up the watch',
        'Matching is case-insensitive and catches PRO DOUBLES WOMEN too',
        'Checks every 5 minutes; polling stops automatically once it hits',
      ],
    },
    screenshots: [
      { alt: 'Watch list with categories being tracked and their check status', caption: 'Every drop you’re chasing, in one place.', aspect: '16/9' },
      { alt: 'A push notification showing a ticket that just went on sale with its price', caption: 'The moment it opens, you know.', aspect: '4/3' },
    ],
  },
  {
    slug: 'contacts',
    configId: 'personal.contacts',
    eyebrow: 'Personal',
    title: 'Contacts',
    tagline: 'The people in your life — remembered properly.',
    status: 'Available',
    localMode: true,
    body: 'One record per person: how to reach them, how you met, whose birthday is coming, and when you last actually talked. Log every coffee, call, and favor as a touchpoint under the person, collect people into households and circles, and let the "falling out of touch" list nudge you before a friendship goes quiet. Link it into your other Quiver apps — your business pipeline can reference these same people without ever copying your address book around.',
    who: 'Anyone whose relationships outgrow their phone\u2019s contacts app — the birthday-rememberers, the holiday-card senders, and anyone who hates asking "wait, how do I know you again?"',
    tracks: [
      'One clean record per person — phone, email, relationship, company, how you met',
      'Birthdays: set a date of birth once; the annual reminder and age derive themselves',
      'Every touchpoint (call, coffee, meal, favor, gift) logged under the person',
      'Last-contacted, computed from your actual touch history',
      'Households, families, and circles as groups — holiday-card lists included',
      'A "falling out of touch" radar and a map of where everyone lives',
      'Link it into your other apps: deals, projects, and events reference the same people',
    ],
    example: {
      user: 'Had coffee with Priya today — her daughter just started kindergarten.',
      result: 'Logged a coffee with Priya and saved the detail.',
      actions: [
        'Found the existing Priya (no duplicate created)',
        'Added a coffee touchpoint dated today with the kindergarten note',
        'Last contacted updated automatically',
      ],
    },
    screenshots: [
      { alt: 'People directory with phone, email, relationship, and birthday columns', caption: 'Your whole address book, auditable at a glance.', aspect: '16/9' },
      { alt: 'A contact with touch history, birthday reminder, and groups', caption: 'One person, one record — with the whole story.', aspect: '4/3' },
      { alt: 'Overview with touches per month, falling-out-of-touch list, and a map', caption: 'Who you\u2019re seeing — and who you\u2019re losing.', aspect: '4/3' },
    ],
  },
  {
    slug: 'personal-chef',
    configId: 'business.personal-chef',
    eyebrow: 'Business',
    title: 'Personal Chef',
    tagline: 'Clients, menus, and service days — run your chef business like a kitchen.',
    status: 'Available',
    body: 'Keep every client\u2019s tastes, allergies, and household in one place; build your dish library once and compose menus per service; then run each cooking day as a file that carries the plan, the shop, and the results. Quiver keeps the numbers — services this month, revenue, favorite dishes — while you keep the burners on.',
    who: 'Private and personal chefs juggling a handful of households, each with its own preferences, allergies, and cadence.',
    tracks: [
      'Clients with household details, dietary constraints, and preferences',
      'A reusable dish library — tags, cuisines, and prep notes',
      'Services (cook days) from planning through completed, menu attached',
      'Meals prepared per service, linked back to the dish library',
      'Revenue and service cadence per client, kept live',
    ],
    example: {
      user: 'Book the Hendersons for next Thursday — tacos night plus that flourless cake.',
      result: 'Scheduled the service and drafted the menu.',
      actions: [
        'Created Thursday\u2019s service for the Henderson household',
        'Menu drafted from your dish library (tacos, flourless chocolate cake)',
        'Allergy notes from their client file carried onto the plan',
      ],
    },
    screenshots: [
      { alt: 'Service day file with menu, shopping notes, and status flow', caption: 'A cook day as one file — plan, shop, serve.', aspect: '16/9' },
      { alt: 'Dish library with tags and cuisines', caption: 'Build the library once; compose menus forever.', aspect: '4/3' },
    ],
  },
  {
    slug: 'massage-therapy',
    configId: 'business.massage-therapy',
    eyebrow: 'Business',
    title: 'Massage Therapy',
    tagline: 'A calm book of business — clients, appointments, and care notes.',
    status: 'Available',
    body: 'Every client\u2019s history in one place — focus areas, pressure preferences, contraindications — with appointments that flow from booked to completed and notes that carry forward, so session twelve remembers what session one learned. See your week, your regulars, and your revenue without a spreadsheet in sight.',
    who: 'Independent massage therapists and small studios who want client care — not admin — to be the craft.',
    tracks: [
      'Clients with preferences, focus areas, and contraindications',
      'Appointments from booked through completed, with per-session notes',
      'Session cadence per client — who\u2019s due back, who\u2019s gone quiet',
      'Revenue and appointment volume, kept live',
    ],
    example: {
      user: 'Maria came in for 60 minutes — shoulders again, lighter pressure this time.',
      result: 'Logged Maria\u2019s session with the care notes.',
      actions: [
        'Completed today\u2019s appointment for Maria (60 min)',
        'Noted shoulders focus and the lighter-pressure preference on her file',
        'Her next-visit suggestion updated',
      ],
    },
    screenshots: [
      { alt: 'Appointment day view with client history alongside', caption: 'The session and the story, side by side.', aspect: '16/9' },
      { alt: 'Client file with preferences and contraindications', caption: 'What session one learned, session twelve remembers.', aspect: '4/3' },
    ],
  },
]

export function getTemplate(slug: string): MarketingTemplate | undefined {
  return templates.find((t) => t.slug === slug)
}

/**
 * Company-wide legal pages — owned by Quiver Labs, not by any single template.
 * These are the canonical, entity-level documents (privacy policy, etc.) that
 * partners and reviewers (e.g. Plaid, OAuth banks) require at the company level.
 * Rendered by the same generic /legal/<slug> route, just without a template
 * association. Per-template / per-config policies still live on each template.
 */
export const companyLegalPages: LegalPage[] = [
  {
    slug: 'privacy',
    title: 'Privacy Policy',
    description:
      'How Quiver Labs collects, uses, and protects your data.',
    version: '2026-05-31',
    sections: [
      {
        id: 'who-we-are',
        title: 'Who we are',
        body: `Quiver Labs ("Quiver", "we", "us") builds and operates the Quiver workspace platform, available at <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="https://quiveros.ai">quiveros.ai</a>. This Privacy Policy explains what data we collect, how we use it, and the choices you have. It applies to all Quiver workspaces. Some features let you connect an external service to bring in your own data; where a feature relies on a third-party provider, we describe it in its own section below.

We are the data controller for the personal information described here. For privacy questions or requests, contact us at <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="mailto:hello@quiveros.ai">hello@quiveros.ai</a>.`,
      },
      {
        id: 'what-we-collect',
        title: 'What we collect',
        body: `- **Account information you give us** — your name, email, and authentication details when you create an account or sign in (including via Google, if you choose social login).
- **Content you create** — the files, notes, and other data you enter into your workspace.
- **Data from services you connect** — if you use a feature that links an external service, we receive the data needed to power that feature. We use it only to provide that feature to you. Specifics are described in the relevant section below.
- **Technical data** — basic logs needed to operate and secure the service (e.g. IP address, device/browser type, timestamps).`,
      },
      {
        id: 'how-we-use',
        title: 'How we use your data',
        body: `We use your data **only to provide and improve the service to you** — the person who owns the workspace. Specifically, we use it to store and organize the content you create, compute the metrics, charts, and history your dashboards show, and operate and secure the platform.

We do **not** use your data for advertising, and we do **not** build profiles of you to sell. We use the data you bring in solely to deliver the features you asked for as the account owner.`,
      },
      {
        id: 'no-sale',
        title: 'We do not sell or rent your data',
        body: `**We do not sell, rent, or trade your personal data to anyone.** We do not share it with third parties except the limited service providers (sub-processors) listed below, who process it only on our behalf and under contract, or where you direct us to, or where required by law.`,
      },
      {
        id: 'sharing',
        title: 'Service providers (sub-processors)',
        body: `We rely on a small set of vetted providers to run the service. They may process your data only as needed to provide their service to us, under confidentiality obligations:

- **Infrastructure & hosting** — our cloud hosting and object storage providers, where workspace data is stored and served.
- **AI features (optional)** — when you use an AI feature, the specific data needed for that request may be sent to a third-party large language model provider operating under inference-only terms. These providers are contractually prohibited from training their models on your content. AI features are optional, and data is sent only when a request you make requires it.
- **Connected-service providers** — where you connect an external service, the provider that powers that connection (see the relevant section below).

We do not use any external advertising network, data broker, or analytics provider that would receive your data.`,
      },
      {
        id: 'storage-location',
        title: 'Where your data lives',
        body: `Today, Quiver runs as a hosted service. Your data is stored in our own per-workspace database behind access controls — not in any third-party vector store, RAG store, or analytics platform.

We are building a **local-first** option (coming soon) that will let you run Quiver on your own instance, so data too personal to keep in someone else's cloud can stay on your machine. We will update this policy when that option is available.`,
      },
      {
        id: 'retention',
        title: 'Data retention & deletion',
        body: `We retain your data for the life of your workspace so your history remains available to you. You are always in control:

- Disconnect any connected service at any time to stop new data from being pulled.
- Delete individual files, or delete your workspace, to remove the underlying data.
- Request deletion of your account and associated personal data by contacting <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="mailto:hello@quiveros.ai">hello@quiveros.ai</a>. We will purge the requested data within 30 days and confirm when done.`,
      },
      {
        id: 'your-rights',
        title: 'Your data, your rights',
        body: `It's your data. We give every user — everywhere, regardless of where you live — full rights over it, not just the legal minimum:

- **Access & export** — get a copy of all of your data, in a portable form, at any time.
- **Correct** — fix anything that's wrong.
- **Delete** — delete any part of your data, or your entire account, and we will remove it. No dark patterns, no hostage-holding.
- **Restrict & object** — tell us to stop or limit a particular use of your data.

To exercise any of these, contact <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="mailto:hello@quiveros.ai">hello@quiveros.ai</a> — or, for many of these, do it yourself directly in the app. We will never discriminate against you for exercising these rights. We build for the people whose data it is.`,
      },
      {
        id: 'connected-data-sources',
        title: 'Connected data sources & specific policies',
        body: `Some templates let you bring in data from an external source. Because each source has its own handling rules and provider terms, we keep a dedicated policy for each one. If your workspace uses one of these, that policy governs the data it covers, in addition to this one:

- **Financial accounts** — when you connect your bank, card, loan, or investment accounts (e.g. in the personal finance template), we use Plaid as the data provider. See our <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="/legal/financial-data">Financial Account Data Policy</a> and Plaid's own <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="https://plaid.com/legal/#end-user-privacy-policy">End User Privacy Policy</a>.
- **Real-estate listings (MLS)** — for workspaces licensed against a Multiple Listing Service feed, see our <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="/legal/mls-data-retention">MLS Data Retention Policy</a>.

If your workspace doesn't use one of these sources, the corresponding policy simply doesn't apply to you.`,
      },
      {
        id: 'security',
        title: 'Security',
        body: `We protect your data with encryption in transit and at rest, access controls scoped to your workspace, and an encrypted secret store for credentials and tokens. No system is perfectly secure, but we work to safeguard your information and to limit who and what can access it. If you believe your account has been compromised, contact us immediately.`,
      },
      {
        id: 'changes',
        title: 'Changes to this policy',
        body: `We may update this policy as the service evolves. Material changes will be reflected by a new document version (shown at the top of this page) and, where appropriate, communicated to you. Continued use of the service after an update constitutes acceptance of the revised policy.`,
      },
      {
        id: 'contact',
        title: 'Contact us',
        body: `Questions, requests, or concerns about your privacy? Reach Quiver Labs at <a class="text-indigo-600 hover:underline dark:text-indigo-400" href="mailto:hello@quiveros.ai">hello@quiveros.ai</a>. We respond within 5 business days.`,
      },
    ],
  },
]

/** Flatten company-wide + per-template legalPages into a slug → {template, page}
 *  index. Powers the generic /legal/<slug> route and the footer's legal-links
 *  list. Company pages have a null template. Slugs are globally unique. */
export function allLegalPages(): {
  template: MarketingTemplate | null
  page: LegalPage
}[] {
  const out: { template: MarketingTemplate | null; page: LegalPage }[] = []
  for (const p of companyLegalPages) out.push({ template: null, page: p })
  for (const t of templates) {
    for (const p of t.legalPages ?? []) out.push({ template: t, page: p })
  }
  return out
}

export function findLegalPage(
  slug: string,
): { template: MarketingTemplate | null; page: LegalPage } | undefined {
  return allLegalPages().find((entry) => entry.page.slug === slug)
}
